<template>
  <div class="externalFinancingForm flex flex-col gap-4">
    <PaymentFormSelect
      v-model="formData.lender"
      label="Lender"
      placeholder="Select a lender"
      :options="lenderOptions"
      :error-message="firstErrorMessage(v$.lender)"
      data-cy="lender"
    />

    <PaymentFormInput
      v-if="customLender"
      v-model="formData.customLender"
      label="Lender name"
      placeholder="Lender name"
      :error-message="firstErrorMessage(v$.customLender)"
      data-cy="custom-lender"
    />

    <div v-if="!isValidationSupported">
      <PaymentFormInput
        v-model="formData.loanNumber"
        label="Loan #"
        placeholder="Loan #"
        :error-message="firstErrorMessage(v$.loanNumber)"
        data-cy="loan-number"
      />
    </div>
    <div v-else class="flex items-end gap-3">
      <div class="flex-1 min-w-0">
        <PaymentFormInput
          v-model="formData.loanNumber"
          label="Loan #"
          placeholder="Loan #"
          :error-message="firstErrorMessage(v$.loanNumber)"
          data-cy="loan-number"
        />
      </div>
      <button
        v-if="showValidateLoanNumber"
        type="button"
        data-cy="validate-button"
        :disabled="isValidating"
        class="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 disabled:opacity-60 disabled:cursor-not-allowed"
        @click="validateLoanNumber"
      >
        {{ validatingText }}
      </button>
      <div
        v-else
        class="flex items-center gap-2 pb-2 text-sm"
        :class="
          lastValidatedState.errorMessage ? 'text-red-600' : 'text-green-700'
        "
      >
        <i
          :class="
            lastValidatedState.errorMessage
              ? 'fa-regular fa-triangle-exclamation'
              : 'fa-regular fa-badge-check'
          "
          aria-hidden="true"
        />
        <span v-if="lastValidatedState.errorMessage" data-cy="error-message">{{
          lastValidatedState.errorMessage
        }}</span>
        <span v-else data-cy="validated-message">Validated</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dollars } from "serviceshift-ui/shared/src/mixins";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import PaymentFormSelect from "@/components/PaymentModals/PaymentFormSelect.vue";
import { Lenders } from "@/Financing/types";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import usePaymentMethods from "../usePaymentMethods";

const props = defineProps({
  invoiceId: {
    type: Number,
    default: null
  },
  paymentAmountCents: {
    type: Number,
    default: 0
  }
});

const store = useStore();

const formData = ref({
  lender: "",
  customLender: "",
  loanNumber: ""
});
const hasValidated = ref(false);
const isValidating = ref(false);
const lastValidatedState = ref({
  errorMessage: "",
  loanNumber: "",
  lender: "",
  paymentAmountCents: props.paymentAmountCents
});

const v$ = useVuelidate(
  {
    lender: {
      required: helpers.withMessage("Lender is required", required),
      $autoDirty: true
    },
    customLender: {
      required: helpers.withMessage(
        "Lender name is required",
        requiredIf(() => formData.value.lender === "other")
      ),
      $autoDirty: true
    },
    loanNumber: {
      required: helpers.withMessage("Loan # is required", required),
      $autoDirty: true
    }
  },
  formData.value
);
const { validateFinancingLoanNumber, paymentAmountErrorMessage } =
  usePaymentMethods();

const lenderOptions = [
  {
    label: "Service Finance",
    value: "service_finance"
  },
  {
    label: "Other",
    value: "other"
  }
];

const customLender = computed(() => {
  return formData.value.lender === "other";
});

const serviceFinanceEnabled = computed(() => {
  return store.getters.serviceFinanceEnabled;
});

// Validation is currently disabled until the backend API is fully functional
const isValidationSupported = computed(() => {
  return false;
});

const validatingText = computed(() => {
  if (isValidating.value) return "Validating…";
  return "Validate loan #";
});

const showValidateLoanNumber = computed(() => {
  return (
    lastValidatedState.value.paymentAmountCents !== props.paymentAmountCents ||
    lastValidatedState.value.lender !== formData.value.lender ||
    lastValidatedState.value.loanNumber !== formData.value.loanNumber
  );
});

onMounted(() => {
  if (serviceFinanceEnabled.value) {
    formData.value.lender = "service_finance";
  }
});

async function validateLoanNumber() {
  await validate();
  isValidating.value = true;
  paymentAmountErrorMessage.value = "";
  try {
    const response = await validateFinancingLoanNumber({
      invoiceId: props.invoiceId,
      loanNumber: formData.value.loanNumber,
      lenderKey: formData.value.lender,
      paymentAmountCents: props.paymentAmountCents
    });
    if (response.result === "valid") {
      lastValidatedState.value = {
        ...formData.value,
        errorMessage: "",
        paymentAmountCents: props.paymentAmountCents
      };
    } else {
      lastValidatedState.value = {
        ...formData.value,
        errorMessage: response.message,
        paymentAmountCents: props.paymentAmountCents
      };
      if (response.message.includes("amount differs")) {
        paymentAmountErrorMessage.value = `${response.message}: ${dollars(
          response.amount_cents
        )}`;
        lastValidatedState.value.errorMessage = "Loan amount mismatch";
      }
    }
  } catch (e) {
    // Do nothing for now
  }
  isValidating.value = false;
  hasValidated.value = true;
}

async function validate() {
  const validated = await v$.value.$validate();
  if (!validated) throw new Error("invalid");
  return true;
}

function getData() {
  return {
    ...formData.value,
    transaction_type: "external_financing",
    processor_name: processorName(),
    reference_id: formData.value.loanNumber
  };
}

function processorName() {
  if (formData.value.lender === "other") {
    return formData.value.customLender;
  } else {
    return Lenders[formData.value.lender].name;
  }
}

defineExpose({ validate, getData });
</script>
